import {EditorState, type Extension, RangeSetBuilder, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'

import {type CstNode, parseMarkdown, walkCst} from '../parse-markdown'
import styles from './decorate-markdown.module.css'

function overlapsNode(selectionFrom: number, selectionTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (selectionFrom >= nodeFrom && selectionFrom < nodeTo) ||
    (selectionTo > nodeFrom && selectionTo <= nodeTo) ||
    (selectionFrom < nodeFrom && selectionTo > nodeTo)
  )
}

function getNodeMark(
  node: CstNode,
  active: boolean,
  source: string,
  withinActiveTable: boolean,
): Decoration | undefined {
  const blockClass = clsx(styles.block, active && styles.activeBlock)

  switch (node.type) {
    case 'blockQuoteMarker':
    case 'strongSequence':
    case 'emphasisSequence':
    case 'codeTextSequence': // inline code delimiters
    case 'strikethroughSequence':
    case 'codeFencedFence':
    case 'codeTextPadding':
    case 'atxHeadingSequence': // ### heading indicator
    case 'setextHeadingLine': // === or --- heading underline
    case 'labelMarker': // brackets around link text
    case 'resource': // link (url) part
    case 'linePrefix': // indentation for indented code blocks
    case 'tableCellDivider':
      return Decoration.mark({class: styles.delimiter})
    case 'codeFencedFenceInfo':
      return Decoration.mark({class: styles.codeInfo})
    case 'strong':
      return Decoration.mark({tagName: 'strong'})
    case 'emphasis':
      return Decoration.mark({tagName: 'em'})
    case 'codeText':
      return Decoration.mark({tagName: 'code'})
    case 'strikethrough':
      return Decoration.mark({tagName: 'del'})
    case 'link': {
      const destinationNode = node.children
        .find(n => n.type === 'resource')
        ?.children.find(n => n.type === 'resourceDestination')
      return Decoration.mark({
        tagName: 'a',
        attributes: {
          href: destinationNode ? source.slice(destinationNode.start, destinationNode.end) : '',
        },
      })
    }
    case 'thematicBreak':
      return Decoration.mark({tagName: 'hr', class: blockClass})
    case 'blockQuote':
      return Decoration.mark({tagName: 'blockquote', class: blockClass})
    case 'codeFenced':
    case 'codeIndented':
      return Decoration.mark({tagName: 'pre', class: blockClass})
    case 'paragraph':
      return Decoration.mark({tagName: 'p', class: clsx(blockClass, styles.paragraph)})
    case 'atxHeading': {
      const sequence = node.children.find(n => n.type === 'atxHeadingSequence')
      if (!sequence) return undefined
      const level = sequence.end - sequence.start
      return Decoration.mark({tagName: `h${level}`, class: blockClass})
    }
    case 'setextHeading': {
      const sequence = node.children.find(n => n.type === 'setextHeadingLine')
      if (!sequence) return undefined
      const level = source.slice(sequence.start, sequence.end).trim().startsWith('=') ? 1 : 2
      return Decoration.mark({tagName: `h${level}`, class: blockClass})
    }
    case 'table':
      // tables revert to plain text when active (for now, until we build a table editor)
      return active
        ? Decoration.mark({tagName: 'pre', class: blockClass})
        : Decoration.mark({tagName: 'table', class: clsx(blockClass, styles.table)})
    case 'tableHead':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'thead'})
    case 'tableBody':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'tbody'})
    case 'tableRow':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'tr'})
    case 'tableHeader':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'th'})
    case 'tableData':
    case 'tableDelimiter':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'td'})
    case 'tableDelimiterRow':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'tr', class: styles.delimiter})
    default:
      return undefined
  }
}

function buildDecorations(editorState: EditorState): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()

  // There's only one line in the editor so this styles the entire content (there's probably a more elegant way to do this)
  builder.add(0, 0, Decoration.line({class: clsx('markdown-body', styles.markdown)}))

  // decorating the markdown in one single extension ensures this parsing is only done one time
  const source = editorState.doc.toString()
  const tree = parseMarkdown(source)

  const selection = editorState.selection.ranges

  const activeTables = new Set<CstNode>()

  walkCst(tree, (node, ancestors) => {
    const overlapsSelection = selection.some(range => overlapsNode(range.from, range.to, node.start, node.end))
    if (overlapsSelection && node.type === 'table') activeTables.add(node)

    const mark = getNodeMark(
      node,
      overlapsSelection,
      source,
      ancestors.some(n => activeTables.has(n)),
    )

    if (mark) builder.add(node.start, node.end, mark)
  })

  return builder.finish()
}

const markdownDecorationsState = StateField.define<DecorationSet>({
  create: s => buildDecorations(s),
  // TODO: This should probably map the existing decorations rather than recreating them, which would be faster and
  // allow us to handle selection changes more efficiently
  update: (prev, tr) => (tr.docChanged || tr.newSelection ? buildDecorations(tr.state) : prev),
  provide: f => EditorView.decorations.from(f),
})

export const decorateMarkdownPlugin: Extension = markdownDecorationsState
